"use client";

/**
 * Standard section wrapper for canvas renderers.
 * Provides consistent bg color, max-width, padding, and optional section title.
 * When componentId and titleFieldId are provided, the title becomes inline-editable
 * in builder mode.
 */

import type React from "react";
import { useMemo } from "react";
import { getReadableAccentColor, isValidHexColor } from "@/features/landing-pages/_logic/color-utils";
import { contrastRatio, pickReadableOnBackground, WCAG_AA_NORMAL_TEXT_CONTRAST } from "@/lib/color/contrast";
import { blendHex, hexToRgba } from "@/lib/color/hex";
import { useBuilderContext } from "../../../../_hooks/useBuilderContext";
import { getBackgroundStyle, resolveOverlayOpacity, resolveScrimHex } from "../../../../_logic/background-styles";
import {
  SECTION_BG_IMAGE_RENDER_QUALITY,
  SECTION_BG_IMAGE_RENDER_WIDTH,
  SECTION_BG_IMAGE_SCRIM_DARK_HEX,
  SECTION_BG_IMAGE_SCRIM_LIGHT_HEX,
  SECTION_CARD_SURFACE_HEX,
  SECTION_SURFACE_HOVER_ALPHA,
  SECTION_TEXT_DARK_HEX,
  SECTION_TEXT_LIGHT_HEX,
  SECTION_TEXT_MAX_CONTRAST_HEX,
  SECTION_TEXT_MUTED_BLEND,
} from "../../../../_logic/constants";
import { transformStorageImageUrl } from "../../../../_logic/image-cdn";
import { isValidStorageUrl } from "../../../../_logic/utils";
import type { CardBackground } from "../../../../_types";
import { usePublicViewContext } from "../../../public/PublicViewContext";
import { InlineTextEdit } from "./InlineTextEdit";

/** Style object that also carries CSS custom properties, typed instead of `as`-cast per write. */
type SectionCssVars = React.CSSProperties & { [key: `--${string}`]: string | undefined };

/** The section's resolved surface for contrast purposes. */
interface SectionSurface {
  /** Hex to check text/accent contrast against. */
  contrastBgHex: string;
  /** Readable body text color, or null when there's no override — callers then leave the page's
   *  own `textColor` completely untouched rather than running it through any contrast pick. */
  resolvedTextColor: string | null;
}

/**
 * Picks a readable body text color for `bgHex`: the page's own `textColor` when it already meets
 * WCAG AA against it (preserving brand fidelity), otherwise a bidirectional light/dark pick —
 * with a guaranteed-AA pure-black fallback for the narrow mid-luminance band (roughly 0.17-0.23,
 * e.g. violet `#8b5cf6`) where neither candidate clears the threshold on its own.
 * @param bgHex - The background to check contrast against.
 * @param pageTextColor - The page's configured text color.
 * @returns A readable hex color.
 */
function resolveReadableTextColor(bgHex: string, pageTextColor: string): string {
  if (isValidHexColor(pageTextColor) && contrastRatio(pageTextColor, bgHex) >= WCAG_AA_NORMAL_TEXT_CONTRAST) {
    return pageTextColor;
  }
  const picked = pickReadableOnBackground(bgHex, SECTION_TEXT_LIGHT_HEX, SECTION_TEXT_DARK_HEX);
  return contrastRatio(picked, bgHex) >= WCAG_AA_NORMAL_TEXT_CONTRAST ? picked : SECTION_TEXT_MAX_CONTRAST_HEX;
}

/**
 * Quieter companion to {@link resolveReadableTextColor} for secondary text (captions, list
 * markers, lead paragraphs), so an override doesn't flatten every tier of the section's type
 * scale into one flat color. Blends the resolved text color back toward the background, but only
 * keeps the result while it still clears AA — muting never costs legibility.
 * @param textHex - The section's resolved full-strength text color.
 * @param bgHex - The background the text sits on.
 * @returns A muted hex color, or `textHex` when muting it would drop below AA.
 */
function resolveMutedTextColor(textHex: string, bgHex: string): string {
  const muted = blendHex(bgHex, textHex, SECTION_TEXT_MUTED_BLEND);
  return contrastRatio(muted, bgHex) >= WCAG_AA_NORMAL_TEXT_CONTRAST ? muted : textHex;
}

/**
 * Picks the hover/selected tint for controls that sit directly on the section (filter chips).
 *
 * The tint is translucent, so it composites into the surface the chip's *own* label is read
 * against — which makes a fixed tint a contrast bug waiting to happen. Tinting toward the text
 * color (the intended "lift", and what this looked like when it shipped) moves the pill toward the
 * label and costs contrast: on a mid-luminance section like indigo `#6366f1` or violet `#8b5cf6`,
 * where the label only clears AA by a hair to begin with, 12% is enough to drop it to 3.8:1 — the
 * chip becomes unreadable exactly while you're pointing at it.
 *
 * So: keep the lift while it holds AA, and flip to the opposite end when it doesn't. The flipped
 * tint moves the pill *away* from the label, so it can only raise contrast, and it's the same 12%
 * either way — the affordance reads the same.
 *
 * There's no third "give up" branch, because the flip can't fail: `textHex` always clears AA
 * against `bgHex` (`resolveReadableTextColor` guarantees it), and moving the surface further from
 * the label only widens that. Swept over the RGB cube at step 3 against five page text colors
 * (3.18M pairs) the flip never dropped below AA — the tightest was 4.507:1. That margin is thin
 * enough that it's the test, not the reasoning, that protects it: the sweep in
 * `SectionContainer.test.tsx` is what catches a future change to the alpha or to the light/dark
 * candidates.
 * @param textHex - The section's resolved text color, which is also the chip label's color.
 * @param bgHex - The surface the tint composites over.
 * @returns An `rgba()` tint that keeps the label at AA.
 */
function resolveSurfaceHoverTint(textHex: string, bgHex: string): string {
  if (contrastRatio(textHex, blendHex(textHex, bgHex, SECTION_SURFACE_HOVER_ALPHA)) >= WCAG_AA_NORMAL_TEXT_CONTRAST) {
    return hexToRgba(textHex, SECTION_SURFACE_HOVER_ALPHA);
  }
  return hexToRgba(
    pickReadableOnBackground(textHex, SECTION_TEXT_LIGHT_HEX, SECTION_TEXT_DARK_HEX),
    SECTION_SURFACE_HOVER_ALPHA
  );
}

/**
 * Resolves the section's effective background (for contrast checks) and, when there's an
 * override, a readable body text color for it — one early return per override kind instead of a
 * nested ternary chain.
 * @param override - The section's background override, or undefined for none.
 * @param encodedImageUrl - The override's encoded image URL, when it's a valid image background.
 * @param scrimIsDark - Whether the image scrim (see `SectionContainer`) is the dark variant.
 * @param effectiveBg - The section's background when there's no override.
 * @param pageTextColor - The page's configured text color.
 * @returns The section's contrast background and resolved text color.
 */
function resolveSectionSurface(
  override: CardBackground | undefined,
  encodedImageUrl: string | null,
  scrimIsDark: boolean,
  effectiveBg: string,
  pageTextColor: string
): SectionSurface {
  if (override?.kind === "solid" && isValidHexColor(override.color)) {
    return {
      contrastBgHex: override.color,
      resolvedTextColor: resolveReadableTextColor(override.color, pageTextColor),
    };
  }
  if (encodedImageUrl) {
    const scrimHex = scrimIsDark ? SECTION_BG_IMAGE_SCRIM_DARK_HEX : SECTION_BG_IMAGE_SCRIM_LIGHT_HEX;
    return { contrastBgHex: scrimHex, resolvedTextColor: resolveReadableTextColor(scrimHex, pageTextColor) };
  }
  return { contrastBgHex: effectiveBg, resolvedTextColor: null };
}

interface SectionContainerProps {
  /** Background color variant — used as a fallback when `background` is unset or "none" */
  bg?: "white" | "gray-50";
  /**
   * Section background override — solid color, uploaded image, or gradient. Takes over from `bg`
   * whenever its kind isn't "none"/unset. A scrim (light or dark, matching the page's text color)
   * is composited under an image background so text stays legible over an arbitrary photo; text
   * color for a solid override is likewise recomputed for contrast against it and exposed as the
   * `--lp-text`/`--lp-text-muted` CSS variables (only when an override is set) so descendant
   * content that hardcodes its own text color can opt in via
   * `text-[color:var(--lp-text,<original color>)]`, with the fallback keeping a section that has
   * no override on exactly the color it always had.
   *
   * One thing does change without an override: `--lp-accent` is now derived with
   * `getReadableAccentColor`, which lightens or darkens toward whichever end actually contrasts,
   * where the previous helper only ever darkened. On a dark-palette page the old one walked the
   * accent toward black against a dark background (an invisible link), so the difference there is
   * the fix, not a regression.
   *
   * What that does NOT license is moving a call site that used the raw `primaryColor` onto one of
   * these vars. The accent vars are published unconditionally, and the correction is not a no-op
   * on a light background — amber `#f59e0b` resolves to `#a54e00`, a pastel `#C6D2F7` to
   * `#626e93`. `--lp-accent` is only a safe substitution where the old code was already running
   * the accent through `getReadableTextColor` (the `prose-a:` link colors, which predate this PR);
   * anywhere else it repaints every published page on deploy. The team and sponsor names are back
   * on raw `primaryColor` for exactly this reason.
   */
  background?: CardBackground;
  /** Max width of inner container */
  maxWidth?: "3xl" | "4xl" | "5xl" | "6xl";
  /** Optional section title rendered as h2 */
  title?: string;
  /** Component ID — enables inline title editing in builder mode */
  componentId?: string;
  /** Field ID for inline edit tracking (e.g., "pricing-title") */
  titleFieldId?: string;
  /** Section content */
  children: React.ReactNode;
}

/** Maps bg prop to its computed hex value (used for accent contrast checks + the gray-50 variant) */
const BG_HEX_MAP = {
  white: "#ffffff",
  "gray-50": "#f9fafb",
} as const;

/** Maps maxWidth prop to Tailwind class */
const MAX_WIDTH_MAP = {
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
} as const;

/**
 * Wraps renderer content with consistent outer padding, centering, and optional heading.
 * Pass componentId + titleFieldId to make the title inline-editable in builder mode.
 * @param bg - Fallback background variant used when `background` is unset or "none".
 * @param background - Section background override — see the prop's own doc above.
 * @param maxWidth - Max width of the inner content container.
 * @param title - Optional section title, rendered as an h2.
 * @param componentId - Component ID — enables inline title editing in builder mode (with titleFieldId).
 * @param titleFieldId - Field ID for inline edit tracking (e.g., "pricing-title").
 * @param children - Section content.
 * @returns The rendered section wrapper.
 */
export function SectionContainer({
  bg = "white",
  background,
  maxWidth = "3xl",
  title,
  componentId,
  titleFieldId,
  children,
}: SectionContainerProps): React.ReactElement {
  const { isPublic } = usePublicViewContext();
  const { primaryColor, backgroundColor, textColor } = useBuilderContext();
  const isInlineEditable = !isPublic && !!componentId && !!titleFieldId;

  // The palette `backgroundColor` themes the default (white) section variant; the alternating
  // "gray-50" separator keeps its fixed tint. Defaults (#ffffff / near-black) reproduce the
  // pre-palette look, so existing pages are unchanged.
  const effectiveBg = bg === "white" ? backgroundColor : BG_HEX_MAP[bg];

  // A solid/gradient override is a flat CSS value (validated in `getBackgroundStyle`); an image
  // override needs the storage-URL check + CDN resize used everywhere else images render (see
  // HeroRenderer). `isValidStorageUrl` (not the looser `isSafeUrl`) matches the check the server
  // applies on save and every other image-rendering path in this feature — an arbitrary http(s)
  // host would let anyone with edit access make every attendee load a third-party image.
  const override = background && background.kind !== "none" ? background : undefined;
  // Memoized on its own — `getBackgroundStyle` returns a new object every call, and this feeds
  // the `containerStyle` memo below as a dependency; without memoizing here first, that outer
  // memo would never hit its cache for a solid/gradient override.
  const overrideStyle = useMemo(
    () => (override ? getBackgroundStyle(override, primaryColor) : null),
    [override, primaryColor]
  );
  const safeImageUrl =
    override?.kind === "image" && override.url && isValidStorageUrl(override.url) ? override.url : null;
  const encodedImageUrl = useMemo(() => {
    if (!safeImageUrl) return null;
    const resolved = transformStorageImageUrl(safeImageUrl, {
      width: SECTION_BG_IMAGE_RENDER_WIDTH,
      quality: SECTION_BG_IMAGE_RENDER_QUALITY,
    });
    return encodeURI(resolved).replace(/['()]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  }, [safeImageUrl]);

  // A background image's scrim tone tracks the page's own text color, not a hardcoded white — a
  // white wash reads fine under dark text but erases light text on a dark-themed palette exactly
  // as badly as no scrim erases dark text on a dark photo. Picked from the page's base `textColor`
  // (not the final resolved one below) so the two derivations don't depend on each other.
  const scrimIsDark = !!encodedImageUrl && resolveScrimHex(textColor) === SECTION_BG_IMAGE_SCRIM_DARK_HEX;

  // User-adjustable strength (0-100) of the scrim above — the editor's "Overlay" slider on
  // `SectionBackgroundField`. Defaults to the original fixed 65% wash when unset.
  const overlayOpacityPercent = resolveOverlayOpacity(override);

  // The image case is a known approximation — contrast is checked against the scrim's flat tone
  // assuming it's fully opaque, when it's actually composited at `overlayOpacityPercent`, so a
  // low overlay over a brightly-colored photo can still under-deliver contrast; there's no way to
  // know the photo's actual colors without sampling its pixels, so this is the closest static
  // guess available.
  const { contrastBgHex, resolvedTextColor } = resolveSectionSurface(
    override,
    encodedImageUrl,
    scrimIsDark,
    effectiveBg,
    textColor
  );

  // Accent for content on its own opaque light surface (a card, a white button) rather than on the
  // section itself. Checked against the card's own surface, not the section's and not the page's:
  // a card is white whatever sits behind it, so that's the only background its text is ever read
  // against. Correcting against the page background instead looks equivalent on a light page but
  // silently breaks a dark-palette one — it lightens the accent for a dark page background, then
  // renders it on a white card at roughly 3.8:1.
  const cardAccent = useMemo(() => getReadableAccentColor(primaryColor, SECTION_CARD_SURFACE_HEX), [primaryColor]);

  // Split from `containerStyle` so it isn't recomputed (up to 20 lighten/darken steps) on every
  // overlay-slider tick, which the style memo below does depend on.
  const sectionAccent = useMemo(
    () => getReadableAccentColor(primaryColor, contrastBgHex),
    [primaryColor, contrastBgHex]
  );

  const containerStyle = useMemo(() => {
    const base: SectionCssVars = {
      "--lp-accent": sectionAccent,
      "--lp-accent-on-card": cardAccent,
      // `resolvedTextColor` is only non-null once there's an override (see `resolveSectionSurface`)
      // — a section with no override keeps the page's configured `textColor` completely untouched,
      // never run through any contrast pick.
      color: resolvedTextColor ?? textColor,
    };
    // `--lp-text`/`--lp-text-muted` are exposed only when an override is set (i.e.
    // `resolvedTextColor` is non-null), so descendant content that hardcodes its own text color
    // (e.g. `text-gray-900`) can opt in via `text-(--lp-text,<original>)`; the fallback
    // keeps a section with no override reading the same CSS it always has.
    if (resolvedTextColor) {
      base["--lp-text"] = resolvedTextColor;
      base["--lp-text-muted"] = resolveMutedTextColor(resolvedTextColor, contrastBgHex);
      // Hover/selected tint for controls that sit directly on the section (filter chips). A fixed
      // `bg-gray-100` pill only reads on a light section, but swapping it for a ring everywhere
      // changed the affordance on pages that don't use this feature at all — so the tint is a
      // variable that only exists once there's an override, contrast-checked against the chip's own
      // label (see `resolveSurfaceHoverTint`) so it works on light and dark alike.
      base["--lp-surface-hover"] = resolveSurfaceHoverTint(resolvedTextColor, contrastBgHex);
    }

    if (encodedImageUrl) {
      const scrimHex = scrimIsDark ? SECTION_BG_IMAGE_SCRIM_DARK_HEX : SECTION_BG_IMAGE_SCRIM_LIGHT_HEX;
      const scrim = hexToRgba(scrimHex, overlayOpacityPercent / 100);
      return {
        ...base,
        backgroundImage: `linear-gradient(${scrim}, ${scrim}), url('${encodedImageUrl}')`,
        // Painted under the photo so the section isn't blank while it loads (or if it 404s) —
        // the scrim's own tone, which is also what the text color above was picked against, so
        // text stays readable in that state instead of landing on whatever is behind the section.
        backgroundColor: scrimHex,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }

    if (overrideStyle) return { ...base, ...overrideStyle };

    return { ...base, backgroundColor: effectiveBg };
  }, [
    sectionAccent,
    cardAccent,
    resolvedTextColor,
    overrideStyle,
    effectiveBg,
    textColor,
    encodedImageUrl,
    contrastBgHex,
    scrimIsDark,
    overlayOpacityPercent,
  ]);

  return (
    <div className="px-4 py-12" style={containerStyle}>
      <div className={`mx-auto flex flex-col gap-6 ${MAX_WIDTH_MAP[maxWidth]}`}>
        {title !== undefined && isInlineEditable ? (
          <InlineTextEdit
            tag="h2"
            value={title || ""}
            placeholder="Click to add title..."
            className="text-center text-2xl font-bold md:text-4xl"
            componentId={componentId}
            fieldId={titleFieldId}
            field="title"
          />
        ) : (
          title && <h2 className="text-center text-2xl font-bold md:text-4xl">{title}</h2>
        )}
        {children}
      </div>
    </div>
  );
}
